import { useEffect, useState } from "react";

/** How often the stuck clock advances. A minute is the resolution the copy uses. */
const TICK_MS = 60_000;

/**
 * A `Date.now()` that re-renders every minute — the input to every `now`
 * parameter in `slotStage`.
 *
 * Stuck-ness is a function of elapsed time, not of any data change, so nothing
 * would otherwise re-render to reveal the recovery button: polling stops telling
 * us anything new precisely when a run has died. This is the only clock that
 * makes "it has been 11 minutes" observable.
 *
 * `enabled` keeps the interval off wherever nothing is in a machine state, so a
 * settled month costs no timers at all. The cost of that gate: when it flips on
 * after a long quiet spell the value is up to a minute behind, so a recovery
 * button can appear a minute late. That direction is the safe one — an old `now`
 * makes elapsed time look SHORTER, so the button is never offered too early.
 */
export function useStuckClock(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [enabled]);

  return now;
}

export default useStuckClock;
